<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Option {
  value: string | number
  label: string
}

const props = defineProps<{
  modelValue: string | number
  options: Option[]
  placeholder?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const triggerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const dropdownStyle = ref({ top: '0px', left: '0px', width: '0px' })

// Get the label for the currently selected value
const selectedLabel = computed(() => {
  const selected = props.options.find((opt) => opt.value === props.modelValue)
  return selected?.label || ''
})

// Filter options based on search query
const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options
  }
  const query = searchQuery.value.toLowerCase()
  return props.options.filter((opt) => opt.label.toLowerCase().includes(query))
})

// Update dropdown position
const updateDropdownPosition = () => {
  if (triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect()
    dropdownStyle.value = {
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`
    }
  }
}

// Select an option
const selectOption = (option: Option) => {
  emit('update:modelValue', option.value)
  isOpen.value = false
  searchQuery.value = ''
}

// Toggle dropdown
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    searchQuery.value = ''
    updateDropdownPosition()
    nextTick(() => {
      searchInputRef.value?.focus()
    })
  }
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node
  const clickedTrigger = triggerRef.value?.contains(target)
  const clickedDropdown = dropdownRef.value?.contains(target)

  if (!clickedTrigger && !clickedDropdown) {
    isOpen.value = false
    searchQuery.value = ''
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="relative">
    <!-- Selected value display / trigger -->
    <div
      ref="triggerRef"
      @click="toggleDropdown"
      class="w-full px-2 py-1.5 text-sm border rounded bg-metal-800 border-metal-700 hover:border-metal-600 text-metal-300 cursor-pointer flex justify-between items-center"
    >
      <span :class="{ 'text-metal-500': !selectedLabel }">
        {{ selectedLabel || placeholder || t('common.placeholders.select') }}
      </span>
      <svg
        class="w-4 h-4 transition-transform text-gray-500"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <!-- Dropdown (teleported to body) -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="fixed z-[100] bg-metal-900 border border-metal-700 rounded shadow-lg"
        :style="dropdownStyle"
      >
        <!-- Search input -->
        <div class="p-2 border-b border-metal-700">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            :placeholder="t('common.placeholders.search')"
            class="w-full p-2 text-sm border rounded bg-metal-800 border-metal-700 hover:border-metal-600 text-metal-300 placeholder-metal-500 focus:outline-none focus:ring-1 focus:ring-metal-700 focus:border-metal-500"
            @click.stop
          />
        </div>

        <!-- Options list -->
        <div class="max-h-48 overflow-y-auto">
          <div
            v-for="option in filteredOptions"
            :key="option.value"
            @click="selectOption(option)"
            class="px-3 py-2 text-sm text-metal-300 cursor-pointer hover:bg-blue-600"
            :class="{ 'bg-metal-600': option.value === modelValue }"
          >
            {{ option.label }}
          </div>
          <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-sm text-metal-400">
            {{ t('common.status.noResults') }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss"></style>
